// Read a whole chapter as one continuous page, grouped by lesson.
import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { actions, getProgress, settings } from '../lib/store.js';
import { chapter, loadChapter, chapterSky } from '../lib/content.js';
import { topbar } from '../components/chrome.js';
import { verseBlock } from '../components/verse.js';
import notfound from './notfound.js';

const TOGGLE_LABELS = { sanskrit: 'Original Sanskrit', iast: 'Transliteration' };

// The English always shows. These chips open the Sanskrit fold on every
// verse (and remember it), and choose whether it includes transliteration.
export function displayToggles() {
  const s = settings();
  const chip = (key) => html`<button type="button" class="chip" data-toggle="${key}" aria-pressed="${s[key] ? 'true' : 'false'}">${icon(s[key] ? 'eye' : 'eye-off', { size: 16 })} ${TOGGLE_LABELS[key]}</button>`;
  return html`<div class="display-toggles" role="group" aria-label="Show with each verse">
    ${chip('sanskrit')}
    ${s.sanskrit ? chip('iast') : ''}
  </div>`;
}

export function bindDisplayToggles(root) {
  const group = root.querySelector('.display-toggles');
  if (!group) return;
  group.addEventListener('click', (e) => {
    const b = e.target.closest('[data-toggle]');
    if (!b) return;
    const key = b.dataset.toggle;
    const on = !settings()[key];
    actions.setSettings({ [key]: on });
    if (key === 'sanskrit') {
      for (const d of root.querySelectorAll('.verse__original')) d.open = on;
    }
    group.outerHTML = String(displayToggles());
    bindDisplayToggles(root);
  });
}

export default async function read({ ch: raw }) {
  const ch = Number(raw);
  if (!(ch >= 1 && ch <= 18 && String(ch) === raw)) return notfound();
  const meta = chapter(ch);
  const data = await loadChapter(ch);
  const sky = chapterSky(ch);
  const prev = ch > 1 ? chapter(ch - 1) : null;
  const next = ch < 18 ? chapter(ch + 1) : null;
  return {
    title: `Read chapter ${ch}`,
    html: html`<div class="reader" style="--sky-top:${sky.top};--sky-bottom:${sky.bottom};--sky-ink:${sky.ink}">
      ${topbar({ back: `/chapter/${ch}`, title: `Chapter ${ch} · ${meta.title}` })}
      <header class="wrap page-head reader__head">
        <p class="eyebrow">Chapter ${ch} · ${meta.verses} verses</p>
        <h1 class="h1">${meta.title}</h1>
        <p class="deva reader__sa" lang="sa">${meta.sa}</p>
        ${displayToggles()}
      </header>
      <div class="wrap">
        ${meta.lessons.map((l) => html`<section class="reader__lesson" aria-labelledby="rl-${l.id}">
          <header class="reader__lesson-head">
            <p class="eyebrow">Lesson ${l.n} · ${l.from === l.to ? `Verse ${l.from}` : `Verses ${l.from} to ${l.to}`}</p>
            <h2 class="h2" id="rl-${l.id}">${l.title}</h2>
            <p class="muted">${l.idea}</p>
          </header>
          <div class="reader__verses">
            ${data.verses.filter((v) => v.v >= l.from && v.v <= l.to).map((v) => verseBlock(ch, v, { mode: 'reader', headingLevel: 3 }))}
          </div>
          <a class="link reader__lesson-link" href="/lesson/${l.id}">${getProgress().lessons[l.id] ? 'Review this lesson' : 'Take this lesson'} ${icon('arrow-right', { size: 16 })}</a>
        </section>`)}
        <nav class="pager section" aria-label="Chapters">
          ${prev ? html`<a class="pager__link" href="/read/${prev.ch}">${icon('arrow-left', { size: 18 })}<span><span class="pager__label">Chapter ${prev.ch}</span><span class="pager__title">${prev.title}</span></span></a>` : html`<span></span>`}
          ${next ? html`<a class="pager__link pager__link--next" href="/read/${next.ch}"><span><span class="pager__label">Chapter ${next.ch}</span><span class="pager__title">${next.title}</span></span>${icon('arrow-right', { size: 18 })}</a>` : ''}
        </nav>
      </div>
    </div>`,
    mount(root) {
      bindDisplayToggles(root);
      // A verse counts as read once it has been on screen for a moment.
      const timers = new Map();
      const seen = new IntersectionObserver((entries) => {
        for (const e of entries) {
          const id = e.target.dataset.verse;
          if (e.isIntersecting) {
            if (!timers.has(id)) timers.set(id, setTimeout(() => { actions.markVerse(id); seen.unobserve(e.target); }, 1500));
          } else if (timers.has(id)) {
            clearTimeout(timers.get(id));
            timers.delete(id);
          }
        }
      }, { threshold: 0.6 });
      const progress = getProgress();
      for (const el of root.querySelectorAll('[data-verse]')) if (!progress.verses[el.dataset.verse]) seen.observe(el);
      return () => {
        seen.disconnect();
        for (const t of timers.values()) clearTimeout(t);
      };
    },
  };
}
